import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Country, State, City } from 'country-state-city';
import Swal from 'sweetalert2'; // Added import for SweetAlert2

const AddAccount = ({
  formData: propFormData,
  handleChange: propHandleChange,
  countries: propCountries,
  selectedCountryIso: propSelectedCountryIso,
  selectedStateIso: propSelectedStateIso,
  stateList: propStateList,
  handleCountryChange: propHandleCountryChange,
  handleStateChange: propHandleStateChange,
  onSave
}) => {
  // Use props if available, otherwise use local state
  const isStandalone = !propFormData;
  const [localFormData, setLocalFormData] = useState({
    account_type: "",
    account_name: "",
    address: "",
    address2: "",
    country: "",
    state: "",
    city: "",
    zip: "",
    telephone: "",
    email: "",
    website: "",
    time_zone: "",
    status: "",
    ddxEnable: false,
    addedBy: "", // Added field
  });
  const formData = isStandalone ? localFormData : propFormData;
  // Local state for dropdowns if not provided via props
  const countries = propCountries || Country.getAllCountries();
  const [selectedCountryIso, setSelectedCountryIso] = useState(propSelectedCountryIso || '');
  const [selectedStateIso, setSelectedStateIso] = useState(propSelectedStateIso || '');
  const [stateList, setStateList] = useState(propStateList || []);
  const [cityList, setCityList] = useState([]);
  const navigate = useNavigate();
  const prevCountryIso = useRef(selectedCountryIso);
  const prevStateIso = useRef(selectedStateIso);
  // State for custom states (per country) and add UI
  const [customStates, setCustomStates] = useState({}); // { countryIso: [{isoCode, name, countryCode}, ...] }
  const [showAddState, setShowAddState] = useState(false);
  const [newStateName, setNewStateName] = useState('');
  // Get LabId from localStorage
  const [labId, setLabId] = useState('');
  // NEW: Load customStates from localStorage on mount
  useEffect(() => {
    const storedCustoms = localStorage.getItem('customStates');
    if (storedCustoms) {
      setCustomStates(JSON.parse(storedCustoms));
    }
  }, []);
  // Get LabId from localStorage on component mount
  useEffect(() => {
    const storedLabId = localStorage.getItem('userLabId');
    if (storedLabId) {
      setLabId(storedLabId);
     
      // If in standalone mode, update formData with labId as addedBy
      if (isStandalone) {
        setLocalFormData(prev => ({
          ...prev,
          addedBy: storedLabId
        }));
      }
    }
  }, [isStandalone]);
  // Sync props to local state
  useEffect(() => {
    setSelectedCountryIso(propSelectedCountryIso || '');
  }, [propSelectedCountryIso]);
  useEffect(() => {
    setSelectedStateIso(propSelectedStateIso || '');
  }, [propSelectedStateIso]);
  useEffect(() => {
    setStateList(propStateList || []);
  }, [propStateList]);
  // Handle country change effects (Merge with custom states and sort)
  useEffect(() => {
    if (selectedCountryIso) {
      const baseStates = propStateList || State.getStatesOfCountry(selectedCountryIso);
      const customs = customStates[selectedCountryIso] || [];
      const combined = [...baseStates, ...customs].sort((a, b) => a.name.localeCompare(b.name));
      setStateList(combined);
      if (selectedCountryIso !== prevCountryIso.current) {
        setSelectedStateIso('');
        setCityList([]);
        if (isStandalone) {
          setLocalFormData(prev => ({
            ...prev,
            state: '',
            city: ''
          }));
        } else if (propHandleChange) {
          propHandleChange({ target: { name: 'state', value: '' } });
          propHandleChange({ target: { name: 'city', value: '' } });
        }
        if (propHandleStateChange && selectedStateIso !== '') {
          propHandleStateChange({ target: { value: '' } });
        }
      }
    } else {
      setStateList([]);
      setCityList([]);
    }
    prevCountryIso.current = selectedCountryIso;
  }, [selectedCountryIso, customStates, isStandalone, propHandleChange, propHandleStateChange, propStateList]); // Added customStates to deps
  // Handle state change effects
  useEffect(() => {
    if (selectedCountryIso && selectedStateIso) {
      const cities = City.getCitiesOfState(selectedCountryIso, selectedStateIso);
      setCityList(cities || []);
      if (selectedStateIso !== prevStateIso.current) {
        if (isStandalone) {
          setLocalFormData(prev => ({
            ...prev,
            city: ''
          }));
        } else if (propHandleChange) {
          propHandleChange({ target: { name: 'city', value: '' } });
        }
      }
    } else {
      setCityList([]);
    }
    prevStateIso.current = selectedStateIso;
  }, [selectedCountryIso, selectedStateIso, isStandalone, propHandleChange]);
  // Handler to add a new custom state (UPDATED: Save to localStorage after update)
  const handleAddState = () => {
    const trimmedName = newStateName.trim();
    if (!trimmedName) {
      alert("Please enter a state name.");
      return;
    }
    const exists = stateList.some(s => s.name.toLowerCase() === trimmedName.toLowerCase());
    if (exists) {
      alert("This state already exists.");
      return;
    }
    const newIso = `CU_${trimmedName.replace(/\s+/g, '_').toUpperCase()}`;
    const newState = { isoCode: newIso, name: trimmedName, countryCode: selectedCountryIso };
    // Update customStates
    const updatedCustoms = {
      ...customStates,
      [selectedCountryIso]: [...(customStates[selectedCountryIso] || []), newState]
    };
    setCustomStates(updatedCustoms);
    // Save to localStorage
    localStorage.setItem('customStates', JSON.stringify(updatedCustoms));
    // Manually update stateList (add and sort)
    const updatedList = [...stateList, newState].sort((a, b) => a.name.localeCompare(b.name));
    setStateList(updatedList);
    // Automatically select the new state
    handleStateChange({ target: { value: newIso } });
    // Reset add UI
    setShowAddState(false);
    setNewStateName('');
  };
  const handleLocalChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (isStandalone) {
      setLocalFormData(prev => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    } else if (propHandleChange) {
      propHandleChange(e);
    }
  };
  const handleCountryChange = (e) => {
    const iso = e.target.value;
    const country = countries.find(c => c.isoCode === iso);
    const countryName = country ? country.name : '';
    if (propHandleCountryChange) {
      propHandleCountryChange(e);
    } else {
      setSelectedCountryIso(iso);
    }
    if (isStandalone) {
      setLocalFormData(prev => ({
        ...prev,
        country: countryName
      }));
    } else if (propHandleChange) {
      propHandleChange({ target: { name: 'country', value: countryName } });
    }
  };
  const handleStateChange = (e) => {
    const iso = e.target.value;
    const state = stateList.find(s => s.isoCode === iso);
    const stateName = state ? state.name : '';
    if (propHandleStateChange) {
      propHandleStateChange(e);
    } else {
      setSelectedStateIso(iso);
    }
    if (isStandalone) {
      setLocalFormData(prev => ({
        ...prev,
        state: stateName
      }));
    } else if (propHandleChange) {
      propHandleChange({ target: { name: 'state', value: stateName } });
    }
  };
  const handleCityChange = (e) => {
    const cityName = e.target.value;
    if (isStandalone) {
      setLocalFormData(prev => ({
        ...prev,
        city: cityName
      }));
    } else if (propHandleChange) {
      propHandleChange({ target: { name: 'city', value: cityName } });
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
   
    // Ensure addedBy is populated with labId
    const formDataToSubmit = {
      ...formData,
      addedBy: labId || formData.addedBy
    };
   
    // Validate that addedBy is present
    if (!formDataToSubmit.addedBy) {
      alert("Cannot create account: Lab ID not found. Please login again.");
      return;
    }
   
    try {
      if (onSave) {
        onSave(formDataToSubmit);
        // Optional: Show alert here too if needed for prop-based mode
        await Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Account added successfully!',
          confirmButtonText: 'OK',
          confirmButtonColor: '#004aad' // Match your button color
        });
      } else {
        const response = await axios.post(
          "https://hms.backend.hiragroupcloud.com/api/account/add-account",
          formDataToSubmit
        );
       
        // Show SweetAlert2 success popup
        await Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Account added successfully!',
          confirmButtonText: 'OK',
          confirmButtonColor: '#004aad' // Match your button color
        });
       
        // Navigate after alert is closed
        navigate("/accounts");
      }
    } catch (error) {
      console.error("There was an error submitting the form:", error);
      // Optional: Use SweetAlert for error too
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to create account. Please try again.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#004aad'
      });
    }
  };
  return (
    <div className="text-section">
      <h3 style={{ marginLeft: "35px" }}>Add New Account</h3>
      <form
        onSubmit={handleSubmit}
        style={{
          borderTop: "3px solid #004aad",
          backgroundColor: "#f4f4f4",
          width: "95%",
          margin: "0 auto",
          padding: "20px",
        }}
      >
        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label>
                Account Type <span className="text-danger">*</span>
              </label>
              <select
                name="account_type"
                value={formData.account_type}
                onChange={handleLocalChange}
                className="form-control"
                required
              >
                <option value="">Select...</option>
                <option value="Practice">Practice</option>
                <option value="Lab">Lab</option>
              </select>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label>
                Account Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                name="account_name"
                value={formData.account_name}
                onChange={handleLocalChange}
                className="form-control"
                required
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label>
                Address <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleLocalChange}
                className="form-control"
                required
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label>Address 2</label>
              <input
                type="text"
                name="address2"
                value={formData.address2}
                onChange={handleLocalChange}
                className="form-control"
              />
            </div>
          </div>
        </div>
        {/* Country Dropdown */}
        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label>
                Country <span className="text-danger">*</span>
              </label>
              <select
                value={selectedCountryIso}
                onChange={handleCountryChange}
                className="form-control"
                required
                style={{ backgroundColor: 'white' }}
              >
                <option value="">Select a country</option>
                {countries.map((country) => (
                  <option key={country.isoCode} value={country.isoCode}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        
          {/* State Dropdown (Add button and input for new state) */}
          <div className="col-md-6">
            <div className="form-group">
              <label>
                State / Province <span className="text-danger">*</span>
              </label>
              <select
                value={selectedStateIso}
                onChange={handleStateChange}
                disabled={!selectedCountryIso}
                className="form-control"
                required
                style={{ backgroundColor: 'white' }}
              >
                <option value="">Select a state</option>
                {stateList.map((state) => (
                  <option key={state.isoCode} value={state.isoCode}>
                    {state.name}
                  </option>
                ))}
              </select>
              {/* Button to show add input */}
              {selectedCountryIso && !showAddState && (
                <button
                  type="button"
                  onClick={() => setShowAddState(true)}
                  className="btn btn-secondary mt-2"
                  style={{ backgroundColor: "#004aad", borderColor: "#004aad" }}
                >
                  Add New State
                </button>
              )}
              {/* Input and buttons for adding state */}
              {showAddState && (
                <div className="mt-2">
                  <input
                    type="text"
                    value={newStateName}
                    onChange={(e) => setNewStateName(e.target.value)}
                    placeholder="Enter new state name"
                    className="form-control mb-2"
                  />
                  <button
                    type="button"
                    onClick={handleAddState}
                    className="btn btn-success mr-2"
                    style={{ backgroundColor: "#004aad", borderColor: "#004aad", marginRight: "10px" }}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddState(false);
                      setNewStateName('');
                    }}
                    className="btn btn-danger"
                    style={{ backgroundColor: "#dc3545", borderColor: "#dc3545" }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="row">
          {/* City Input or Dropdown */}
          <div className="col-md-6">
            <div className="form-group">
              <label>
                City <span className="text-danger">*</span>
              </label>
              {selectedStateIso && cityList.length > 0 ? (
                <select
                  name="city"
                  value={formData.city}
                  onChange={handleCityChange}
                  className="form-control"
                  required
                  style={{ backgroundColor: 'white' }}
                >
                  <option value="">Select a city</option>
                  {cityList.map((city) => (
                    <option key={city.name} value={city.name}>
                      {city.name}
                    </option>
                  ))}
                </select>
              ) : selectedStateIso ? (
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleLocalChange}
                  className="form-control"
                  style={{ backgroundColor: 'white' }}
                  placeholder="Enter city name"
                  required
                />
              ) : (
                <select
                  disabled
                  className="form-control"
                  style={{ backgroundColor: 'white' }}
                >
                  <option value="">Select a city</option>
                </select>
              )}
            </div>
          </div>
        
          <div className="col-md-6">
            <div className="form-group">
              <label>Zip / Postal Code</label>
              <input
                type="text"
                name="zip"
                value={formData.zip}
                onChange={handleLocalChange}
                className="form-control"
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label>
                Telephone <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                name="telephone"
                value={formData.telephone}
                onChange={handleLocalChange}
                className="form-control"
                required
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleLocalChange}
                className="form-control"
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label>Website</label>
              <input
                name="website"
                value={formData.website}
                onChange={handleLocalChange}
                className="form-control"
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label>
                Time Zone <span className="text-danger">*</span>
              </label>
              <select
                name="time_zone"
                value={formData.time_zone}
                onChange={handleLocalChange}
                className="form-control"
                required
              >
                <option value="">Select a time zone</option>
                <option value="Asia/Karachi">Asia/Karachi (GMT+5)</option>
                <option value="Asia/Kolkata">Asia/Kolkata (GMT+5:30)</option>
                <option value="Asia/Dhaka">Asia/Dhaka (GMT+6)</option>
                <option value="Asia/Dubai">Asia/Dubai (GMT+4)</option>
                <option value="Europe/London">Europe/London (GMT+0)</option>
                <option value="America/New_York">
                  America/New York (GMT-5)
                </option>
              </select>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label>DDX Enable</label>
              <div className="form-check">
                <input
                  type="checkbox"
                  name="ddxEnable"
                  checked={formData.ddxEnable}
                  onChange={handleLocalChange}
                  className="form-check-input"
                />
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label>
                Status <span className="text-danger">*</span>
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleLocalChange}
                className="form-control"
                required
              >
                <option value="">Select...</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>
        <button
          type="submit"
          className="btn btn-primary"
          style={{ backgroundColor: "#004aad", borderColor: "#004aad" }}
        >
          + Create Account
        </button>
      </form>
    </div>
  );
};
export default AddAccount;